import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';
import ImageUploadField from './ImageUploadField';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const saveSettings = async (updates: { key: string; value: unknown }[]) => {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/bali-save-settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
    body: JSON.stringify({ updates }),
  });
  return res.ok;
};

const SaveBtn = ({ saving, saved, onClick }: { saving: boolean; saved: boolean; onClick: () => void }) => (
  <button onClick={onClick} disabled={saving}
    className={`flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 ${saved ? 'bg-green-500 text-white' : 'bg-yellow-400 hover:bg-yellow-300 text-black'}`}>
    <i className={saving ? 'ri-loader-4-line animate-spin' : saved ? 'ri-check-line' : 'ri-save-line'} />
    {saving ? 'Guardando…' : saved ? '¡Guardado!' : 'Guardar'}
  </button>
);

const SectionTitle = ({ icon, title }: { icon: string; title: string }) => (
  <div className="flex items-center gap-2 mb-5 pb-3 border-b border-zinc-800">
    <div className="w-6 h-6 flex items-center justify-center text-yellow-400"><i className={`${icon} text-base`} /></div>
    <h3 className="text-white font-bold text-sm uppercase tracking-wider">{title}</h3>
  </div>
);

interface EquipoMember {
  id: string;
  nombre: string;
  rol: string;
  especialidad: string;
  experiencia: string;
  imagen: string;
}

interface Servicio {
  id: string;
  nombre: string;
  precio: string;
  oferta: string;
  oferta_activa: boolean;
  imagen: string;
}

const ConfigTab = () => {
  const [taller, setTaller] = useState({ nombre_taller: '', telefono: '', whatsapp: '', email: '', direccion: '', ciudad: '' });
  const [horarios, setHorarios] = useState({ lunes_viernes: '', sabado: '' });
  const [notif, setNotif] = useState({ email: '', enabled: false });
  const [equipo, setEquipo] = useState<EquipoMember[]>([]);
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingMap, setSavingMap] = useState<Record<string, boolean>>({});
  const [savedMap, setSavedMap] = useState<Record<string, boolean>>({});
  const [pwForm, setPwForm] = useState({ current: '', nuevo: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: rows } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('key, value')
        .in('key', ['taller_info', 'horarios', 'notification_email', 'equipo', 'servicios']);
      const map: Record<string, unknown> = {};
      for (const r of rows ?? []) map[r.key] = r.value;
      if (map.taller_info) setTaller(map.taller_info as typeof taller);
      if (map.horarios) setHorarios(map.horarios as typeof horarios);
      if (map.notification_email) setNotif(map.notification_email as typeof notif);
      if (map.equipo) setEquipo(((map.equipo as EquipoMember[]) ?? []).map((m, i) => ({
        id: m.id ?? `e-${i}`,
        nombre: m.nombre ?? '',
        rol: m.rol ?? '',
        especialidad: (m as EquipoMember & { especialidad?: string }).especialidad ?? '',
        experiencia: m.experiencia ?? '',
        imagen: m.imagen ?? '',
      })));
      if (map.servicios) setServicios(((map.servicios as { nombre: string; precio: string; oferta?: string; oferta_activa?: boolean; imagen?: string }[]) ?? []).map((s, i) => ({
        id: `s-${i}`, nombre: s.nombre ?? '', precio: s.precio ?? '',
        oferta: s.oferta ?? '', oferta_activa: s.oferta_activa ?? false, imagen: s.imagen ?? '',
      })));
      setLoading(false);
    };
    load();
  }, []);

  const doSave = async (section: string, updates: { key: string; value: unknown }[]) => {
    setSavingMap((p) => ({ ...p, [section]: true }));
    await saveSettings(updates);
    setSavingMap((p) => ({ ...p, [section]: false }));
    setSavedMap((p) => ({ ...p, [section]: true }));
    setTimeout(() => setSavedMap((p) => ({ ...p, [section]: false })), 2500);
  };

  const handleChangePw = async (e: FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.nuevo !== pwForm.confirm) { setPwMsg({ type: 'err', text: 'Las contraseñas no coinciden.' }); return; }
    const { data } = await supabase.from('TALLERMECANICOKEVIN_settings').select('value').eq('key', 'admin_password').maybeSingle();
    const stored = typeof data?.value === 'string' ? data.value.replace(/^"|"$/g, '') : '';
    if (stored !== pwForm.current) { setPwMsg({ type: 'err', text: 'Contraseña actual incorrecta.' }); return; }
    await supabase.from('TALLERMECANICOKEVIN_settings').update({ value: pwForm.nuevo, updated_at: new Date().toISOString() }).eq('key', 'admin_password');
    setPwMsg({ type: 'ok', text: 'Contraseña actualizada correctamente.' });
    setPwForm({ current: '', nuevo: '', confirm: '' });
  };

  if (loading) return <div className="flex items-center justify-center h-40"><i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" /></div>;

  return (
    <div className="space-y-8">
      {/* TALLER INFO */}
      <section className="bg-zinc-900 border border-zinc-800 p-6">
        <div className="flex items-start justify-between">
          <SectionTitle icon="ri-store-2-line" title="Información del taller" />
          <SaveBtn saving={!!savingMap.taller} saved={!!savedMap.taller} onClick={() => doSave('taller', [{ key: 'taller_info', value: taller }])} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: 'nombre_taller', label: 'Nombre del taller' },
            { key: 'telefono', label: 'Teléfono' },
            { key: 'whatsapp', label: 'WhatsApp' },
            { key: 'email', label: 'Email' },
            { key: 'direccion', label: 'Dirección' },
            { key: 'ciudad', label: 'Ciudad' },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">{f.label}</label>
              <input value={(taller as Record<string, string>)[f.key] ?? ''}
                onChange={(e) => setTaller((p) => ({ ...p, [f.key]: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors" />
            </div>
          ))}
        </div>
      </section>

      {/* HORARIOS */}
      <section className="bg-zinc-900 border border-zinc-800 p-6">
        <div className="flex items-start justify-between">
          <SectionTitle icon="ri-time-line" title="Horarios" />
          <SaveBtn saving={!!savingMap.horarios} saved={!!savedMap.horarios} onClick={() => doSave('horarios', [{ key: 'horarios', value: horarios }])} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: 'lunes_viernes', label: 'Lunes – Viernes' },
            { key: 'sabado', label: 'Sábado' },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">{f.label}</label>
              <input value={(horarios as Record<string, string>)[f.key] ?? ''}
                onChange={(e) => setHorarios((p) => ({ ...p, [f.key]: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors" />
            </div>
          ))}
        </div>
      </section>


      {/* CAMBIAR CONTRASEÑA */}
      <section className="bg-zinc-900 border border-zinc-800 p-6">
        <SectionTitle icon="ri-lock-password-line" title="Cambiar contraseña" />
        <form onSubmit={handleChangePw} className="space-y-4 max-w-sm">
          {[
            { key: 'current', label: 'Contraseña actual' },
            { key: 'nuevo', label: 'Nueva contraseña' },
            { key: 'confirm', label: 'Confirmar nueva contraseña' },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">{f.label}</label>
              <input type="password" required value={(pwForm as Record<string, string>)[f.key]}
                onChange={(e) => setPwForm((p) => ({ ...p, [f.key]: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors" />
            </div>
          ))}
          {pwMsg && (
            <div className={`flex items-center gap-2 px-3 py-2 border text-xs ${pwMsg.type === 'ok' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
              <i className={pwMsg.type === 'ok' ? 'ri-check-line' : 'ri-error-warning-line'} /> {pwMsg.text}
            </div>
          )}
          <button type="submit" className="flex items-center gap-1.5 px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-lock-line" /> Actualizar contraseña
          </button>
        </form>
      </section>
    </div>
  );
};

export default ConfigTab;
